
import { format } from "date-fns";
import { ru, enUS } from "date-fns/locale";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronDownIcon, ChevronUpIcon, ArrowDownLeft, ArrowUpRight, FileText } from "lucide-react";
import { useState } from "react";
import { EnhancedTransaction } from "@/hooks/useTransactions";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface TransactionHistoryProps {
  transactions: EnhancedTransaction[];
  renderStatus?: (transaction: EnhancedTransaction) => string;
}

const TransactionHistory = ({
  transactions,
  renderStatus
}: TransactionHistoryProps) => {
  const { t, i18n } = useTranslation();
  const [sortConfig, setSortConfig] = useState<{
    key: keyof EnhancedTransaction;
    direction: 'ascending' | 'descending';
  }>({
    key: 'created_at',
    direction: 'descending',
  });

  const sortedTransactions = [...transactions].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  const handleSortChange = (key: keyof EnhancedTransaction) => {
    setSortConfig(currentConfig => ({
      key,
      direction:
        currentConfig.key === key && currentConfig.direction === 'ascending'
          ? 'descending'
          : 'ascending'
    }));
  };

  const formatDate = (dateString: string) => {
    try {
      const dateLocale = i18n.language === 'ru' ? ru : enUS;
      return format(new Date(dateString), 'd MMM yyyy, HH:mm', { locale: dateLocale });
    } catch (e) {
      return dateString;
    }
  };

  const formatMinutes = (transaction: EnhancedTransaction) => {
    const value = transaction.amount;
    const isIncome = transaction.type === 'deposit' || transaction.type === 'bonus';
    const sign = isIncome ? '+' : '-';
    return `${sign}${Math.abs(value)}`;
  };

  const getStatusVariant = (transaction: EnhancedTransaction) => {
    if (transaction.type === 'withdrawal') {
      switch (transaction.withdrawal_status) {
        case 'completed': return 'success';
        case 'frozen': return 'warning'; // or 'default' depending on preference
        case 'cancelled': return 'secondary';
        default: return 'outline';
      }
    } else {
      switch (transaction.payment_status) {
        case 'succeeded': return 'success';
        case 'pending': return 'warning';
        case 'failed': return 'destructive';
        case 'canceled': return 'secondary';
        default: return 'outline';
      }
    }
  };

  // Custom badge styles based on variants since standard badge might not have all colors needed
  const getBadgeClassName = (variant: string) => {
    switch (variant) {
      case 'success': return "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-transparent";
      case 'warning': return "bg-amber-100 text-amber-700 hover:bg-amber-100 border-transparent";
      case 'destructive': return "bg-red-100 text-red-700 hover:bg-red-100 border-transparent";
      case 'secondary': return "bg-muted text-muted-foreground hover:bg-muted border-transparent";
      default: return "border-border text-foreground";
    }
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
      <Table>
        <TableHeader className="bg-muted/40">
          <TableRow className="hover:bg-transparent">
            <TableHead
              className="w-[200px] cursor-pointer"
              onClick={() => handleSortChange('created_at')}
            >
              <div className="flex items-center gap-1 font-medium">
                {t('balance.history.date')}
                {sortConfig.key === 'created_at' && (
                  sortConfig.direction === 'ascending'
                    ? <ChevronUpIcon className="h-3 w-3" />
                    : <ChevronDownIcon className="h-3 w-3" />
                )}
              </div>
            </TableHead>
            <TableHead className="font-medium">{t('balance.history.description')}</TableHead>
            <TableHead className="w-[140px] font-medium">{t('balance.history.status')}</TableHead>
            <TableHead className="w-[120px] text-right font-medium">{t('balance.history.minutes_col')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedTransactions.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={4} className="h-32 text-center">
                <div className="flex flex-col items-center justify-center text-muted-foreground">
                  <FileText className="h-8 w-8 mb-2 opacity-50" />
                  <p>{t('balance.history.empty')}</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            sortedTransactions.map((transaction) => (
              <TableRow key={transaction.id} className="group hover:bg-muted/50">
                <TableCell className="py-4 font-medium text-muted-foreground text-sm">
                  {formatDate(transaction.created_at)}
                </TableCell>

                <TableCell className="py-4">
                  <div className="flex flex-col">
                    <span className="font-medium text-foreground line-clamp-1" title={transaction.comment || ""}>
                      {transaction.comment || t('balance.history.default_operation_name')}
                    </span>
                    {/* Optional: Add subtitle or ID here if needed */}
                  </div>
                </TableCell>

                <TableCell className="py-4">
                  {renderStatus && (
                    <Badge
                      variant="outline"
                      className={cn("font-normal text-xs px-2.5 py-0.5", getBadgeClassName(getStatusVariant(transaction)))}
                    >
                      {renderStatus(transaction)}
                    </Badge>
                  )}
                </TableCell>

                <TableCell className="py-4 text-right">
                  <div className={cn(
                    "flex items-center justify-end gap-1 font-semibold tabular-nums",
                    (transaction.type === 'deposit' || transaction.type === 'bonus') ? "text-emerald-600" : "text-foreground"
                  )}>
                    {(transaction.type === 'deposit' || transaction.type === 'bonus') ? (
                      <ArrowDownLeft className="h-3.5 w-3.5" />
                    ) : (
                      <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    {formatMinutes(transaction)}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Mobile view could be handled via responsive table classes or a separate layout if strictly needed, 
          but standard Table is usually okay on tablets. For strict mobile, hidden/block toggles can be used. 
          Current implementation keeps table structure which is generally robust. */}
    </div>
  );
};

export default TransactionHistory;
